import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SVGCaretLeft from '../../assets/svg/icons/SVGCaretLeft';
import SVGCaretRight from '../../assets/svg/icons/SVGCaretRight';
import { DataShareItemRequirement, Requirements } from './types';

interface PopupProps {
  requirements: Requirements;
}

const Popup = (props: PopupProps) => {
  const { requirements } = props;
  const { t } = useTranslation();
  const [activeList, setActiveList] = useState<DataShareItemRequirement[]>(requirements.children);
  const [parentList, setParentList] = useState<DataShareItemRequirement[]>([]);
  const [title, setTitle] = useState<string>(requirements.popupTitle);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    setIsPopupVisible(true);
  };

  const handleMouseLeave = () => {
    setIsPopupVisible(false);
  };

  const handleItemClick = (item: DataShareItemRequirement) => {
    if (item.children && item.children.length > 0) {
      setParentList(activeList); // Save the current list to the parent stack
      setActiveList(item.children); // Set the active list to the children
      setTitle(item.title); // Update the title
    }
  };

  const handleBackClick = () => {
    setActiveList(parentList); // Restore the parent list
    setParentList([]); // Clear the parent stack
    setTitle(requirements.popupTitle); // Reset the title
  };

  // This effect handles the case when the mouse clicks outside of the popup
  useEffect(() => {
    const checkIfClickedOutside = (e: MouseEvent) => {
      if (isPopupVisible && popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setIsPopupVisible(false);
      }
    };
    document.addEventListener('mousedown', checkIfClickedOutside);

    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  }, [isPopupVisible]);

  return (
    <>
      <span onMouseEnter={handleMouseEnter} className="font-semibold cursor-pointer text-textHyperlink">
        — {t(requirements.title)}
      </span>
      {isPopupVisible && (
        <div
          ref={popupRef}
          onMouseLeave={handleMouseLeave}
          className="absolute mt-6 z-10 w-72 bg-background rounded-xl shadow-lg right-0 mr-2 mb-2"
          onMouseEnter={handleMouseEnter}
        >
          <div className="flex justify-center items-center bg-header p-4 rounded-t-2xl">
            {parentList.length > 0 && (
              <button onClick={handleBackClick} className="text-textParagraph hover:text-primaryHover font-semibold">
                <div className="text-textParagraph py-1 px-3">
                  <SVGCaretLeft />
                </div>
              </button>
            )}
            <h3 className="text-lg font-semibold text-textParagraph pr-3">{t(title)}</h3>
          </div>
          <ul className="list-none">
            {activeList.map((item, index) => (
              <li
                key={index}
                className={`cursor-pointer' last:rounded-b-2xl ${
                  item.children?.length ?? 0 > 0 ? 'hover:text-primaryHover hover:bg-opacity-30' : ''
                }`}
              >
                <button
                  onClick={() => handleItemClick(item)}
                  className="w-full flex-row items-center p-4 justify-center text-textTertiary hover:text-primaryHover flow-root"
                >
                  <span className="float-left flex-wrap text-left">{t(item.title)}</span>
                  {(item.children?.length ?? 0) > 0 && (
                    <div className="float-right items-center justify-center mt-1">
                      <SVGCaretRight />
                    </div>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};

export default Popup;
